import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { SimStock, SimulationState } from "@/hooks/useSimulator";

interface Props {
  stocks: SimStock[];
  simulation: SimulationState;
}

interface TrendSignal {
  ticker: string;
  companyName: string;
  sector: string;
  currentPrice: number;
  sma3: number;
  sma5: number;
  signal: "bullish" | "bearish" | "neutral";
  strength: number;
  support: number;
  resistance: number;
  momentum: number;
  description: string;
}

const TrendDetector = ({ stocks, simulation: sim }: Props) => {
  const history = sim.price_history;

  const signals: TrendSignal[] = stocks.map((stock) => {
    const prices = history.map((h) => h.prices[stock.ticker] || stock.current_price);
    const currentPrice = sim.stock_prices[stock.ticker] || stock.current_price;

    const sma3 = prices.length >= 3 ? prices.slice(-3).reduce((s, v) => s + v, 0) / 3 : currentPrice;
    const sma5 = prices.length >= 5 ? prices.slice(-5).reduce((s, v) => s + v, 0) / 5 : currentPrice;

    const recentPrices = prices.slice(-5);
    const support = recentPrices.length > 0 ? Math.min(...recentPrices) : currentPrice;
    const resistance = recentPrices.length > 0 ? Math.max(...recentPrices) : currentPrice;

    const prevPrice = prices.length >= 2 ? prices[prices.length - 2] : currentPrice;
    const momentum = prevPrice ? ((currentPrice - prevPrice) / prevPrice) * 100 : 0;

    let signal: "bullish" | "bearish" | "neutral" = "neutral";
    let strength = 50;
    let description = "No clear trend direction";

    if (prices.length >= 3) {
      const aboveSma3 = currentPrice > sma3;
      const aboveSma5 = currentPrice > sma5;
      const sma3AboveSma5 = sma3 > sma5;
      const nearSupport = (currentPrice - support) / (resistance - support || 1) < 0.2;
      const nearResistance = (currentPrice - support) / (resistance - support || 1) > 0.8;

      if (aboveSma3 && aboveSma5 && sma3AboveSma5) {
        signal = "bullish";
        strength = Math.min(90, 60 + momentum * 5);
        description = nearResistance ? "Strong uptrend, nearing resistance — watch for breakout" : "Price above moving averages — upward momentum";
      } else if (!aboveSma3 && !aboveSma5 && !sma3AboveSma5) {
        signal = "bearish";
        strength = Math.min(90, 60 + Math.abs(momentum) * 5);
        description = nearSupport ? "Downtrend nearing support — potential bounce" : "Price below moving averages — downward pressure";
      } else if (aboveSma3 && !aboveSma5) {
        signal = "bullish";
        strength = 45;
        description = "Short-term recovery — possible trend reversal forming";
      } else if (!aboveSma3 && aboveSma5) {
        signal = "bearish";
        strength = 45;
        description = "Short-term weakness — watch for confirmation";
      } else {
        description = "Mixed signals — price consolidating near averages";
      }
    }

    return { ticker: stock.ticker, companyName: stock.company_name, sector: stock.sector, currentPrice, sma3, sma5, signal, strength: Math.max(0, Math.min(100, strength)), support, resistance, momentum, description };
  });

  const sorted = [...signals].sort((a, b) => {
    const order = { bullish: 0, bearish: 1, neutral: 2 };
    if (order[a.signal] !== order[b.signal]) return order[a.signal] - order[b.signal];
    return b.strength - a.strength;
  });

  const bullishCount = signals.filter((s) => s.signal === "bullish").length;
  const bearishCount = signals.filter((s) => s.signal === "bearish").length;

  const SignalIcon = ({ signal }: { signal: string }) => {
    if (signal === "bullish") return <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />;
    if (signal === "bearish") return <TrendingDown className="w-3.5 h-3.5 text-red-400" />;
    return <Minus className="w-3.5 h-3.5 text-muted-foreground" />;
  };

  const signalColor = (signal: string) =>
    signal === "bullish" ? "text-emerald-400" : signal === "bearish" ? "text-red-400" : "text-muted-foreground";

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-heading font-bold text-sm text-foreground/90 mb-1">Trend Detection</h3>
        <p className="text-muted-foreground text-[10px]">Moving average crossovers, support/resistance, and momentum</p>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 rounded-xl border border-border/50 bg-emerald-400/5 p-3 text-center">
          <p className="text-emerald-400 font-heading font-bold text-lg">{bullishCount}</p>
          <p className="text-emerald-400/60 text-[10px]">Bullish</p>
        </div>
        <div className="flex-1 rounded-xl border border-border/50 bg-muted/20 p-3 text-center">
          <p className="text-muted-foreground font-heading font-bold text-lg">{signals.length - bullishCount - bearishCount}</p>
          <p className="text-muted-foreground/50 text-[10px]">Neutral</p>
        </div>
        <div className="flex-1 rounded-xl border border-border/50 bg-red-400/5 p-3 text-center">
          <p className="text-red-400 font-heading font-bold text-lg">{bearishCount}</p>
          <p className="text-red-400/60 text-[10px]">Bearish</p>
        </div>
      </div>

      <div className="space-y-1.5">
        {sorted.map((s) => (
          <div
            key={s.ticker}
            className="rounded-xl border border-border/50 bg-muted/20 p-3 hover:bg-muted/30 transition-all"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <SignalIcon signal={s.signal} />
                <span className="font-heading font-bold text-xs text-accent/80">{s.ticker}</span>
                <span className="text-muted-foreground text-[10px] truncate max-w-[100px]">{s.companyName}</span>
              </div>
              <span className={`font-heading font-bold text-xs uppercase ${signalColor(s.signal)}`}>
                {s.signal}
              </span>
            </div>
            <p className="text-muted-foreground text-[10px] mb-2">{s.description}</p>

            <div className="grid grid-cols-4 gap-1 text-[10px]">
              <div>
                <span className="text-muted-foreground/50">Price</span>
                <p className="text-foreground/80 font-heading font-semibold">${s.currentPrice.toFixed(2)}</p>
              </div>
              <div>
                <span className="text-muted-foreground/50">SMA3</span>
                <p className={s.currentPrice > s.sma3 ? "text-emerald-400/80" : "text-red-400/80"}>
                  ${s.sma3.toFixed(2)}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground/50">Support</span>
                <p className="text-muted-foreground">${s.support.toFixed(2)}</p>
              </div>
              <div>
                <span className="text-muted-foreground/50">Resist.</span>
                <p className="text-muted-foreground">${s.resistance.toFixed(2)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrendDetector;
